<template>
  <div>
    <v-container>
      <v-row>
        <v-col cols='12'>
          <div class='mb-4'>
            <span class='subtitle-1 mr-1'>Subject: </span>
            <span class='title'>{{ notice.subject }}</span>
          </div>

          <menu-bar v-if='editor' class="editor__header" :editor="editor" :highlighting='notice.highlights' />
          <editor-content v-if='editor' class="editor__content" :editor="editor" />
          <div class="editor__footer">
            <div class="editor__status">
              <template v-if="changes">
                Un-saved changes
              </template>
              <template v-else>
                All changes saved
              </template>
            </div>
            <v-btn x-small elevation='0' :disabled='!changes' color='primary' :loading='isLoading' @click='update'>Save changes</v-btn>
            <div class="editor__name">
              {{ $store.state.auth.profile.name_display }}
            </div>
          </div>
        </v-col>
      </v-row>
      <div v-if='debug'>{{ output }}</div>
    </v-container>
  </div>
</template>

<script>
import { EditorContent, Editor } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import MenuBar from '../components/MenuBar';
import Mention from '@tiptap/extension-mention';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';

export default {
  name: 'notice-overview',
  data: () => ({
    editor: null
  }),
  props: {
    notice: {
      required: true
    },
    isLoading: {
      required: false,
      type: Boolean,
      default: false
    },
    debug: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  mounted () {
    this.editor = new Editor({
      content: this.notice.text,
      extensions: [
        StarterKit,
        Underline,
        Highlight.configure({ multicolor: true }),
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Mention.configure({
          HTMLAttributes: {
            class: 'mention',
          }
        }),
      ],
    });
  },
  computed: {
    output() {
      return this.editor ? this.editor.getHTML() : {};
    },
    changes() {
      if (this.output != this.notice.text) {
        return true;
      }
      return false;
    }
  },
  methods: {
    update () {
      this.notice.text = this.output;
      this.$emit('update', this.notice);
    }
  },
  beforeDestroy() {
    this.editor.destroy();
  },
  components: {
    'editor-content': EditorContent,
    'menu-bar': MenuBar,
  }
}
</script>

<style lang="scss" scoped>

.editor {
  display: flex;
  flex-direction: column;
  max-height: 400px;
  color: #0D0D0D;
  background-color: white;
  border: 3px solid #0D0D0D;
  border-radius: 0.75rem;

  &__header {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    flex-wrap: wrap;
    padding: 0.25rem;
    border-bottom: 3px solid #0D0D0D;
  }

  &__content {
    padding: 1.25rem 1rem;
    flex: 1 1 auto;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  &__footer {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    white-space: nowrap;
    border-top: 3px solid #0D0D0D;
    font-size: 12px;
    font-weight: 600;
    color: #0D0D0D;
    white-space: nowrap;
    padding: 0.25rem 0.75rem;
  }

  &__status {
    display: flex;
    align-items: center;
    border-radius: 5px;

    &::before {
      content: ' ';
      flex: 0 0 auto;
      display: inline-block;
      width: 0.5rem;
      height: 0.5rem;
      background: rgba(#0D0D0D, 0.5);
      border-radius: 50%;
      margin-right: 0.5rem;
    }
  }

  &__name {
    button {
      background: none;
      border: none;
      font: inherit;
      font-size: 12px;
      font-weight: 600;
      color: #0D0D0D;
      border-radius: 0.4rem;
      padding: 0.25rem 0.5rem;

      &:hover {
        color: #FFF;
        background-color: #0D0D0D;
      }
    }
  }
}

.bubble-menu {
  display: flex;
  background-color: #0D0D0D;
  padding: 0.2rem;
  border-radius: 0.5rem;

  button {
    border: none;
    background: none;
    color: #FFF;
    font-size: 0.85rem;
    font-weight: 500;
    padding: 0 0.2rem;
    opacity: 0.6;

    &:hover,
    &.is-active {
      opacity: 1;
    }
  }
}

</style>

<style lang="scss">

.mention {
  color: #2364AA;
  background-color: rgba(#2364AA, 0.1);
  border-radius: 0.3rem;
  padding: 0.1rem 0.3rem;
}

.ProseMirror {
  > * {
    margin-top: 0.75em;
    margin-left: 4px;
    margin-right: 4px;
  }

  ul,
  ol {
    padding: 0 1rem;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1.1;
  }

  code {
    background-color: rgba(#616161, 0.1);
    color: #616161;
  }

  pre {
    background: #0D0D0D;
    color: #FFF;
    font-family: 'JetBrainsMono', monospace;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;

    code {
      color: inherit;
      padding: 0;
      background: none;
      font-size: 0.8rem;
    }
  }

  mark {
    background-color: #FAF594;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  hr {
    margin: 1rem 0;
  }

  blockquote {
    padding-left: 1rem;
    border-left: 2px solid rgba(#0D0D0D, 0.1);
  }

  hr {
    border: none;
    border-top: 2px solid rgba(#0D0D0D, 0.1);
    margin: 0;
    margin-top: 1rem;
  }

  ul[data-type="taskList"] {
    list-style: none;
    padding: 0;

    li {
      display: flex;
      align-items: center;

      > label {
        flex: 0 0 auto;
        margin-right: 0.5rem;
      }
    }
  }
}

</style>